import 'isomorphic-fetch';
import config from 'config';
import {handleError} from 'helpers';

const ACCESS_TOKEN = process.env.HOT_DEV ? process.env.ACCESS_TOKEN : null;

const IMPERSONATING_USER_ID = window.MHD
  ? window.MHD.Global.Configuration.ImpersonatingUserId
  : null;

export function getApiToken() {
  const apiToken = JSON.parse(localStorage.getItem('mhdApiToken'));
  const currentTime = new Date(Date.now());

  if (
    !apiToken ||
    new Date(apiToken.expiration) < currentTime ||
    (apiToken.token && apiToken.token.access_token && !apiToken.token.isGuest)
  ) {
    const req = {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-Access-Token': ACCESS_TOKEN,
        'X-Impersonating-User-Id': IMPERSONATING_USER_ID,
      },
    };

    return fetch(
      `${window.MHD.Global.Configuration.ApiUrl}/OAuthTokenData`,
      req,
    )
      .then(res => {
        if (res.status === 404 || res.status === 400 || res.status === 401) {
          window.location = './../SimpleSignIn.aspx';
        }
        return res.json();
      })
      .then(token => {
        if (token) {
          const fiveMinutes = 300000; // milliseconds
          const expiration = new Date(
            currentTime.getTime() + token.expires_in * 1000 - fiveMinutes,
          );
          localStorage.setItem(
            'mhdApiToken',
            JSON.stringify({token, expiration}),
          );
          return token;
        }
        window.location = './../SimpleSignIn.aspx';
        return null;
      });
  }
  return Promise.resolve(apiToken.token);
}

export function getTokenData() {
  return getApiToken().then(token => {
    if (token.isGuest) {
      const payload = JSON.parse(atob(token.access_token.split('.')[1]));
      return {...token, tokenData: payload};
    } else {
      return token;
    }
  });
}

export function isTokenGuest() {
  return getApiToken().then(token => {
    return token.isGuest;
  });
}

export function makeRequest(uri, method, headers, body, asJson, raw, signal) {
  const defaultHeaders = {
    Accept: 'application/json',
    'Content-Type':
      method === 'PATCH' ? 'application/json-patch+json' : 'application/json',
  };

  const req = {
    method,
    credentials: 'include',
    headers: { ...defaultHeaders, ...headers },
    signal,
  };
  if (method !== 'GET') {
    req.body = body ? JSON.stringify(body) : undefined;
  }

  return fetch(uri, req).then(res => {
    if (res.status === 500) {
      throw Error('Internal server error');
    }
    if (method === 'PUT' && res.status === 404) {
      throw new Error('Not found');
    }
    if (res.status === 204) {
      return {status: 204, data: null};
    }
    if (res.status === 404) {
      if (method === 'GET') {
        return null;
      }

      if (asJson) {
        return res
          .json()
          .then(d => ({status: res.status, data: d}))
          .catch(() => null);
      }

      return res
        .text()
        .then(d => ({status: res.status, data: d}))
        .catch(() => null);
    }
    if (res.status === 403) {
      return res.text().then(d => {
        throw Error(`Unauthorized: ${d}`);
      });
    }
    if (res.status === 422) {
      return res
        .text()
        .then(d => ({status: res.status, data: d.replace(/"/g, '')}));
    }
    if (res.status === 408 || res.status === 504) {
      console.log('response :', res); // informational for test - will remove when resolved
      throw Error('The response took longer than expected. Please try again.');
    }
    if (raw) {
      return res.blob().then(b => ({status: res.status, data: b}));
    }
    if (method === 'DELETE' || !asJson) {
      return res.text().then(d => ({status: res.status, data: d}));
    }
    return res
      .json()
      .then(d => ({status: res.status, data: d}))
      .catch(() => ({status: res.status, data: {}}));
  });
}

export default function request({
  url = null,
  method = 'GET',
  body = {},
  headers = {},
  useMAPI = false,
  asJson = true,
  fullUri = null,
  batch = false,
  includePortal = true,
  apiVersion = '1.0',
  raw = false,
  signal,
}) {
  if (!useMAPI) {
    return getTokenData().then(token => {
      const headerWithAuth = {
        Authorization: `${token.token_type} ${token.access_token}`,
        ...headers,
      };
      let uri = fullUri;
      if (!fullUri) {
        uri = `${config.apiUrl}v${apiVersion}`;
        if (batch) uri += '/batch';
        if (includePortal) uri += `/portal/${token.tokenData.portal_id}`;
        uri += url;
      }
      return makeRequest(
        uri,
        method,
        token.isGuest ? headerWithAuth : headers,
        body,
        asJson,
        raw,
        signal
      );
    });
  }
  const uri = `${window.MHD.Global.Configuration.ApiUrl}${url}`;
  const headerWithAuth = {
    'X-Access-Token': ACCESS_TOKEN,
    ...headers,
  };
  return makeRequest(uri, method, headerWithAuth, body, asJson, raw, signal);
}

export function mapquestRequest(searchString, coords) {
  let url = `${config.mapQuestAPI}?key=${
    config.mapQuestAPIKey
  }&limit=5&q=${searchString}&collection=address,adminArea`;
  if (coords) {
    url = `${url}&location=${coords.longitude},${coords.latitude}`;
  }
  const methodAndHead = {
    method: 'GET',
    headers: {'Content-Type': 'application/json', Accept: 'application/json'},
  };

  return fetch(url, methodAndHead)
    .then(res => {
      if (res.ok) return res.json();

      return [];
    })
    .then(data => (data ? data.results : []))
    .catch(error => {
      handleError('Mapquest error', error);
      return [];
    });
}

export const timesheetCsvRequest = url =>
  fetch(`${window.MHD.Global.Configuration.ApiUrl}/timesheets/export/${url}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json-patch+json',
      'X-Access-Token': ACCESS_TOKEN,
    },
  });



// WEBPACK FOOTER //
// ./src/api/request.js